import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Space, Tag, Tooltip, Popconfirm, Typography, Select, Switch, Divider, Row, Col, Radio, Dropdown, Modal, Input, message } from 'antd';
import {
  DeleteOutlined, ClearOutlined, BorderOuterOutlined, AppstoreOutlined, AimOutlined,
  DragOutlined, PlusCircleOutlined, DownOutlined, SaveOutlined
} from '@ant-design/icons';
import PoolTableSVG, { BALL_SETS, TABLE_SIZES, GRID_WIDTH, GRID_HEIGHT } from './PoolTableSVG';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { getRackPresets, createRackPreset } from '../../api/services/rackPresetService';

const { Text } = Typography;

// Rack presets for different games
const RACK_PRESETS = {
  '9-ball': {
    name: '9-Ball Raute',
    ballSet: '9-ball',
    description: '1 auf Fußpunkt, 9 in der Mitte',
    // Positions are relative to foot spot (0,0) with scale factor
    getBalls: (footSpotX, footSpotY, ballRadius) => {
      const d = ballRadius * 2.1; // Ball diameter with small gap
      const h = d * 0.866; // Height offset for diamond rows
      return [
        { num: 1, x: footSpotX, y: footSpotY }, // 1 on foot spot
        { num: 2, x: footSpotX - d/2, y: footSpotY + h },
        { num: 3, x: footSpotX + d/2, y: footSpotY + h },
        { num: 4, x: footSpotX - d, y: footSpotY + h*2 },
        { num: 9, x: footSpotX, y: footSpotY + h*2 }, // 9 in center
        { num: 5, x: footSpotX + d, y: footSpotY + h*2 },
        { num: 6, x: footSpotX - d/2, y: footSpotY + h*3 },
        { num: 7, x: footSpotX + d/2, y: footSpotY + h*3 },
        { num: 8, x: footSpotX, y: footSpotY + h*4 },
      ];
    }
  },
  '8-ball': {
    name: '8-Ball Dreieck',
    ballSet: '8-ball',
    description: '8 in der Mitte, Solid/Stripe in Ecken',
    getBalls: (footSpotX, footSpotY, ballRadius) => {
      const d = ballRadius * 2.1;
      const h = d * 0.866;
      // Standard 8-ball rack: 8 in center, solid in one back corner, stripe in other
      return [
        { num: 1, x: footSpotX, y: footSpotY }, // Apex - any ball (usually 1)
        { num: 2, x: footSpotX - d/2, y: footSpotY + h },
        { num: 10, x: footSpotX + d/2, y: footSpotY + h }, // stripe
        { num: 3, x: footSpotX - d, y: footSpotY + h*2 },
        { num: 8, x: footSpotX, y: footSpotY + h*2 }, // 8-ball in center
        { num: 11, x: footSpotX + d, y: footSpotY + h*2 },
        { num: 4, x: footSpotX - d*1.5, y: footSpotY + h*3 },
        { num: 12, x: footSpotX - d/2, y: footSpotY + h*3 },
        { num: 5, x: footSpotX + d/2, y: footSpotY + h*3 },
        { num: 13, x: footSpotX + d*1.5, y: footSpotY + h*3 },
        { num: 6, x: footSpotX - d*2, y: footSpotY + h*4 }, // Solid in corner
        { num: 14, x: footSpotX - d, y: footSpotY + h*4 },
        { num: 7, x: footSpotX, y: footSpotY + h*4 },
        { num: 15, x: footSpotX + d, y: footSpotY + h*4 },
        { num: 9, x: footSpotX + d*2, y: footSpotY + h*4 }, // Stripe in corner
      ];
    }
  },
  'snooker': {
    name: 'Snooker Standard',
    ballSet: 'snooker',
    description: 'Standard Snooker Aufbau',
    getBalls: (footSpotX, footSpotY, ballRadius, tableWidth, tableHeight, playingX, playingY, headSpotY) => {
      const d = ballRadius * 2.1;
      const h = d * 0.866;
      const centerX = footSpotX;

      // Red balls in triangle starting at foot spot, extending toward foot rail (larger Y)
      const reds = [];
      let redNum = 1;
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col <= row; col++) {
          const x = centerX + (col - row/2) * d;
          const y = footSpotY + row * h; // Reds extend toward larger Y (foot rail)
          reds.push({ num: `r${redNum}`, x, y });
          redNum++;
        }
      }

      // Color balls on their spots
      // Pink: directly in front of apex (toward head/player = smaller Y)
      // Black: near foot rail (largest Y)
      // Blue: center spot
      // Brown, Green, Yellow: on baulk line (D) near head
      const dRadius = tableWidth * 0.15;
      const baulkY = headSpotY;

      return [
        ...reds,
        { num: 'pink', x: centerX, y: footSpotY - d }, // Pink just before apex (smaller Y)
        { num: 'black', x: centerX, y: playingY + tableHeight * 0.9 }, // Black near foot rail (large Y)
        { num: 'blue', x: centerX, y: playingY + tableHeight * 0.5 }, // Blue center
        { num: 'brown', x: centerX, y: baulkY }, // Brown center of D (small Y)
        { num: 'green', x: centerX - dRadius, y: baulkY }, // Green left of D
        { num: 'yellow', x: centerX + dRadius, y: baulkY }, // Yellow right of D
      ];
    }
  }
};

/**
 * BallPlacer Component
 * Interactive component for placing balls on a pool table.
 * Supports different ball sets (Pool, Snooker), rack presets, and cue ball hit point.
 */
const BallPlacer = ({
  balls = [],
  onBallsChange,
  tableSize = '9ft',
  ballSet = 'universal',
  onTableSizeChange,
  onBallSetChange,
  scale = 0.3,
  cueHitPoint = null,
  onCueHitPointChange,
  compact = false, // Compact mode for mobile
}) => {
  const [selectedBallType, setSelectedBallType] = useState(0);
  const [selectedBall, setSelectedBall] = useState(null);
  const [showGrid, setShowGrid] = useState(false);
  const [showRack, setShowRack] = useState(true);
  const [showCueHitPoint, setShowCueHitPoint] = useState(false);
  const [mode, setMode] = useState('place'); // 'place' or 'move'
  const cueHitPointRef = useRef(null);

  // Server presets
  const [serverPresets, setServerPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);

  // Save preset modal
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  // Current user for permission check
  const { data: currentUser } = useCurrentUser();
  const isBallEditorAdmin = currentUser?.is_ball_editor_admin || false;

  // Get current ball set configuration
  const currentBallSet = BALL_SETS[ballSet] || BALL_SETS['universal'];
  const availableBalls = currentBallSet.balls;
  const ballColors = currentBallSet.colors;

  // Get table configuration for presets
  const tableConfig = TABLE_SIZES[tableSize] || TABLE_SIZES['9ft'];

  // Calculate table dimensions (same as in PoolTableSVG)
  const tableWidth = tableConfig.width * scale;
  const tableHeight = tableConfig.height * scale;
  const padding = 20;
  const railWidth = 16;
  const frameWidth = 8;
  const playingX = padding + frameWidth + railWidth;
  const playingY = padding + frameWidth + railWidth;
  const playingWidth = tableWidth;
  const playingHeight = tableHeight;
  const centerX = playingX + playingWidth / 2;
  // Head spot at top (smaller Y), Foot spot at bottom (larger Y)
  const headSpotY = playingY + playingHeight / 4;  // Near top (player side)
  const footSpotY = playingY + playingHeight * 3 / 4;  // Near bottom (rack side)
  const ballRadius = 28.5 * scale;

  // Convert SVG coordinates to grid coordinates
  const svgToGrid = (svgX, svgY) => ({
    x: Math.round(((svgX - playingX) / playingWidth) * GRID_WIDTH),
    y: Math.round(((svgY - playingY) / playingHeight) * GRID_HEIGHT),
  });

  // Convert grid coordinates to SVG coordinates (for display)
  const gridToSvg = (gridX, gridY) => ({
    x: playingX + (gridX / GRID_WIDTH) * playingWidth,
    y: playingY + (gridY / GRID_HEIGHT) * playingHeight,
  });

  // Convert balls from grid to SVG coordinates for display in editor
  const displayBalls = balls.map(ball => {
    // Check if ball uses grid coordinates (x between 0-1000, y between 0-2000)
    const isGridCoords = ball.x >= 0 && ball.x <= GRID_WIDTH && ball.y >= 0 && ball.y <= GRID_HEIGHT;
    if (isGridCoords && ball.x <= GRID_WIDTH && ball.y <= GRID_HEIGHT) {
      const pos = gridToSvg(ball.x, ball.y);
      return { ...ball, x: pos.x, y: pos.y };
    }
    return ball;
  });

  // Check which balls are already placed
  const placedBallNums = balls.map(b => b.num);
  const unplacedBalls = availableBalls.filter(n => !placedBallNums.includes(n));

  // Reset selected ball type when ball set changes
  useEffect(() => {
    if (!availableBalls.includes(selectedBallType)) {
      setSelectedBallType(availableBalls[0]);
    }
  }, [ballSet, availableBalls, selectedBallType]);

  // Load server presets
  useEffect(() => {
    const loadServerPresets = async () => {
      setPresetsLoading(true);
      try {
        const presets = await getRackPresets();
        setServerPresets(presets || []);
      } catch (error) {
        console.error('Failed to load rack presets:', error);
      } finally {
        setPresetsLoading(false);
      }
    };
    loadServerPresets();
  }, []);

  // Save current setup as preset
  const handleSavePreset = async () => {
    if (!newPresetName.trim()) {
      message.error('Bitte gib einen Namen für das Preset ein.');
      return;
    }
    if (balls.length === 0) {
      message.error('Keine Bälle platziert.');
      return;
    }

    setSaveLoading(true);
    try {
      await createRackPreset({
        name: newPresetName.trim(),
        description: newPresetDescription.trim(),
        ball_set: ballSet,
        balls: balls,
        cue_hit_point: cueHitPoint,
      });
      message.success('Preset gespeichert!');
      setSaveModalVisible(false);
      setNewPresetName('');
      setNewPresetDescription('');
      // Reload presets
      const presets = await getRackPresets();
      setServerPresets(presets || []);
    } catch (error) {
      console.error('Failed to save preset:', error);
      message.error('Fehler beim Speichern des Presets.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle table click - place new ball (only in place mode)
  const handleTableClick = (x, y) => {
    if (mode !== 'place') return;
    if (selectedBallType === null) return;
    if (placedBallNums.includes(selectedBallType)) return;

    // Convert SVG coordinates to grid coordinates for storage
    const gridPos = svgToGrid(x, y);
    const newBall = {
      num: selectedBallType,
      x: gridPos.x,
      y: gridPos.y,
    };

    const newBalls = [...balls, newBall];
    onBallsChange(newBalls);

    // Auto-select next unplaced ball
    const nextUnplaced = unplacedBalls.filter(n => n !== selectedBallType)[0];
    setSelectedBallType(nextUnplaced !== undefined ? nextUnplaced : null);
  };

  // Handle ball drag (only in move mode)
  const handleBallDrag = (ballNum, newX, newY) => {
    if (mode !== 'move') return;
    // Convert SVG coordinates to grid coordinates for storage
    const gridPos = svgToGrid(newX, newY);
    const newBalls = balls.map(b =>
      b.num === ballNum ? { ...b, x: gridPos.x, y: gridPos.y } : b
    );
    onBallsChange(newBalls);
  };

  // Handle ball click
  const handleBallClick = (ball) => {
    if (mode === 'move') {
      setSelectedBall(selectedBall === ball.num ? null : ball.num);
    }
  };

  // Remove selected ball
  const handleRemoveBall = () => {
    if (selectedBall === null) return;
    const newBalls = balls.filter(b => b.num !== selectedBall);
    onBallsChange(newBalls);
    setSelectedBall(null);
  };

  // Clear all balls
  const handleClearAll = () => {
    onBallsChange([]);
    setSelectedBall(null);
    setSelectedBallType(availableBalls[0]);
  };

  // Apply rack preset
  const applyPreset = (presetKey) => {
    const preset = RACK_PRESETS[presetKey];
    if (!preset) return;

    // Change ball set if needed
    if (onBallSetChange && preset.ballSet !== ballSet) {
      onBallSetChange(preset.ballSet);
    }

    // Generate balls based on preset (these are SVG coordinates)
    const presetBalls = preset.getBalls(
      centerX, footSpotY, ballRadius,
      playingWidth, playingHeight, playingX, playingY, headSpotY
    );

    // Convert to grid coordinates for storage
    const gridBalls = presetBalls.map(ball => {
      const gridPos = svgToGrid(ball.x, ball.y);
      return { ...ball, x: gridPos.x, y: gridPos.y };
    });

    onBallsChange(gridBalls);
  };

  // Get ball display style
  const getBallStyle = (num) => {
    const color = ballColors[num] || ballColors[0];
    const isLight = color.fill === '#FFFFFF' || color.fill === '#FFD700' || color.fill === '#FF69B4';
    return {
      backgroundColor: color.fill,
      border: `2px solid ${color.stroke}`,
      color: isLight ? '#000' : '#fff',
    };
  };

  // Get ball display label
  const getBallLabel = (num) => {
    const color = ballColors[num];
    if (!color) return '';
    if (num === 0) return '⚪';
    if (color.label) return color.label;
    if (typeof num === 'string' && num.startsWith('r')) return 'R';
    return '';
  };

  // Table size options
  const tableSizeOptions = Object.entries(TABLE_SIZES).map(([key, value]) => ({
    value: key,
    label: value.name,
  }));

  // Ball set options
  const ballSetOptions = Object.entries(BALL_SETS).map(([key, value]) => ({
    value: key,
    label: value.name,
  }));

  // Apply server preset
  const applyServerPreset = (preset) => {
    if (onBallSetChange && preset.ball_set !== ballSet) {
      onBallSetChange(preset.ball_set);
    }
    onBallsChange(preset.balls || []);
    if (preset.cue_hit_point && onCueHitPointChange) {
      onCueHitPointChange(preset.cue_hit_point);
      setShowCueHitPoint(true);
    }
  };

  // Preset menu items - combine local and server presets
  const localPresetItems = Object.entries(RACK_PRESETS).map(([key, preset]) => ({
    key: `local_${key}`,
    label: (
      <div>
        <div style={{ fontWeight: 'bold' }}>{preset.name}</div>
        <div style={{ fontSize: 11, color: '#888' }}>{preset.description}</div>
      </div>
    ),
    onClick: () => applyPreset(key),
  }));

  const serverPresetItems = serverPresets.map((preset) => ({
    key: `server_${preset.id}`,
    label: (
      <div>
        <div style={{ fontWeight: 'bold' }}>{preset.name}</div>
        <div style={{ fontSize: 11, color: '#888' }}>
          {preset.description || `${preset.ball_set_display} - ${preset.ball_count} Bälle`}
        </div>
      </div>
    ),
    onClick: () => applyServerPreset(preset),
  }));

  const presetMenuItems = [
    { key: 'local_header', label: <Text type="secondary" style={{ fontSize: 11 }}>Standard-Aufbauten</Text>, disabled: true },
    ...localPresetItems,
    ...(serverPresets.length > 0 ? [
      { type: 'divider' },
      { key: 'server_header', label: <Text type="secondary" style={{ fontSize: 11 }}>Gespeicherte Presets</Text>, disabled: true },
      ...serverPresetItems,
    ] : []),
  ];

  // Handle cue hit point toggle
  const handleCueHitPointToggle = (checked) => {
    setShowCueHitPoint(checked);
    if (checked && !cueHitPoint && onCueHitPointChange) {
      onCueHitPointChange({ x: 0, y: 0 });
    } else if (!checked && onCueHitPointChange) {
      onCueHitPointChange(null);
    }
  };

  // Render cue hit point selector with free placement
  const renderCueHitPointSelector = () => {
    if (!showCueHitPoint) return null;

    const handleSvgClick = (e) => {
      const svg = cueHitPointRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2.4 - 1.2;
      const y = ((e.clientY - rect.top) / rect.height) * 2.4 - 1.2;

      // Clamp to ball radius
      const dist = Math.sqrt(x*x + y*y);
      if (dist <= 0.9 && onCueHitPointChange) {
        onCueHitPointChange({ x, y });
      }
    };

    return (
      <div style={{ marginTop: 8, padding: 8, background: '#f5f5f5', borderRadius: 8 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Queue-Treffpunkt (klicke auf den Ball):
        </Text>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg
            ref={cueHitPointRef}
            width="100"
            height="100"
            viewBox="-1.2 -1.2 2.4 2.4"
            style={{ cursor: 'crosshair' }}
            onClick={handleSvgClick}
          >
            {/* Cue ball outline */}
            <circle cx="0" cy="0" r="1" fill="white" stroke="#ccc" strokeWidth="0.05" />
            {/* Crosshairs */}
            <line x1="-0.9" y1="0" x2="0.9" y2="0" stroke="#eee" strokeWidth="0.02" />
            <line x1="0" y1="-0.9" x2="0" y2="0.9" stroke="#eee" strokeWidth="0.02" />
            {/* Current hit point */}
            {cueHitPoint && (
              <circle
                cx={cueHitPoint.x}
                cy={cueHitPoint.y}
                r="0.12"
                fill="#ff4444"
                stroke="#cc0000"
                strokeWidth="0.03"
              />
            )}
          </svg>
        </div>
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <Button
            size="small"
            onClick={() => onCueHitPointChange && onCueHitPointChange({ x: 0, y: 0 })}
          >
            Mitte
          </Button>
        </div>
      </div>
    );
  };

  // Group balls for display
  const renderBallButtons = () => {
    if (ballSet === 'snooker') {
      const cueBall = availableBalls.filter(n => n === 0);
      const redBalls = availableBalls.filter(n => typeof n === 'string' && n.startsWith('r'));
      const colorBalls = availableBalls.filter(n => ['yellow', 'green', 'brown', 'blue', 'pink', 'black'].includes(n));

      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Spielball:</Text>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {cueBall.map(num => renderBallButton(num))}
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Rote ({redBalls.length}):</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {redBalls.map(num => renderBallButton(num))}
            </div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>Farben:</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {colorBalls.map(num => renderBallButton(num))}
            </div>
          </div>
        </>
      );
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {availableBalls.map(num => renderBallButton(num))}
      </div>
    );
  };

  const renderBallButton = (num) => {
    const isPlaced = placedBallNums.includes(num);
    const isSelected = selectedBallType === num;
    const color = ballColors[num];
    const name = color?.name || `Ball ${num}`;

    return (
      <Tooltip key={num} title={isPlaced ? `${name} - bereits platziert` : `${name} platzieren`}>
        <Button
          size="small"
          shape="circle"
          disabled={isPlaced || mode !== 'place'}
          style={{
            ...getBallStyle(num),
            opacity: isPlaced ? 0.4 : (mode !== 'place' ? 0.6 : 1),
            boxShadow: isSelected && mode === 'place' ? '0 0 0 3px #1890ff' : 'none',
            width: 28,
            height: 28,
            padding: 0,
            fontSize: 10,
            fontWeight: 'bold',
            lineHeight: '24px',
          }}
          onClick={() => !isPlaced && mode === 'place' && setSelectedBallType(num)}
        >
          {getBallLabel(num)}
        </Button>
      </Tooltip>
    );
  };

  // Determine game type from ball set for rack shape
  const getGameTypeFromBallSet = () => {
    if (ballSet === '9-ball') return '9-ball';
    if (ballSet === 'snooker') return 'snooker';
    return '8-ball';
  };

  return (
    <Card
      title={compact ? null : "Ball-Platzierung"}
      size="small"
      bordered={!compact}
      styles={compact ? { body: { padding: 0 } } : undefined}
      extra={!compact ? (
        <Space>
          <Text type="secondary">{balls.length} / {availableBalls.length}</Text>
          <Popconfirm
            title="Alle Bälle entfernen?"
            onConfirm={handleClearAll}
            okText="Ja"
            cancelText="Nein"
          >
            <Button size="small" icon={<ClearOutlined />} danger disabled={balls.length === 0}>
              Löschen
            </Button>
          </Popconfirm>
        </Space>
      ) : null}
    >
      {/* Compact header for mobile */}
      {compact && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          padding: '0 4px',
        }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {balls.length} / {availableBalls.length} Bälle
          </Text>
          <Popconfirm
            title="Alle Bälle entfernen?"
            onConfirm={handleClearAll}
            okText="Ja"
            cancelText="Nein"
          >
            <Button size="small" icon={<ClearOutlined />} danger disabled={balls.length === 0}>
              {compact ? '' : 'Löschen'}
            </Button>
          </Popconfirm>
        </div>
      )}

      {/* Mode Toggle */}
      <div style={{ marginBottom: compact ? 8 : 12 }}>
        <Radio.Group
          value={mode}
          onChange={e => setMode(e.target.value)}
          size="small"
          buttonStyle="solid"
          style={{ width: compact ? '100%' : 'auto' }}
        >
          <Radio.Button value="place" style={compact ? { width: '50%', textAlign: 'center' } : undefined}>
            <PlusCircleOutlined /> {compact ? '' : 'Platzieren'}
          </Radio.Button>
          <Radio.Button value="move" style={compact ? { width: '50%', textAlign: 'center' } : undefined}>
            <DragOutlined /> {compact ? '' : 'Verschieben'}
          </Radio.Button>
        </Radio.Group>
      </div>

      {/* Table Settings */}
      <Row gutter={[8, 8]} style={{ marginBottom: compact ? 8 : 12 }}>
        <Col xs={12} sm={8}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {!compact && <Text type="secondary" style={{ fontSize: 11 }}>Tisch:</Text>}
            <Select
              value={tableSize}
              onChange={onTableSizeChange}
              options={tableSizeOptions}
              style={{ width: '100%' }}
              size="small"
              placeholder="Tisch"
            />
          </Space>
        </Col>
        <Col xs={12} sm={8}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {!compact && <Text type="secondary" style={{ fontSize: 11 }}>Ballsatz:</Text>}
            <Select
              value={ballSet}
              onChange={(val) => {
                onBallSetChange && onBallSetChange(val);
                onBallsChange([]);
              }}
              options={ballSetOptions}
              style={{ width: '100%' }}
              size="small"
              placeholder="Ballsatz"
            />
          </Space>
        </Col>
        <Col xs={24} sm={8}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {!compact && <Text type="secondary" style={{ fontSize: 11 }}>Preset:</Text>}
            <Dropdown menu={{ items: presetMenuItems }} trigger={['click']}>
              <Button size="small" style={{ width: '100%' }}>
                Aufbau laden <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      {/* Toggle options */}
      <div style={{
        marginBottom: compact ? 8 : 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: compact ? 6 : 12,
        alignItems: 'center',
        justifyContent: compact ? 'space-around' : 'flex-start',
      }}>
        <Tooltip title="Hilfsraster anzeigen">
          <Space size={4}>
            <Switch size="small" checked={showGrid} onChange={setShowGrid} />
            <Text type="secondary" style={{ fontSize: compact ? 10 : 11 }}>
              <AppstoreOutlined />
            </Text>
          </Space>
        </Tooltip>
        <Tooltip title="Rack-Position anzeigen">
          <Space size={4}>
            <Switch size="small" checked={showRack} onChange={setShowRack} />
            <Text type="secondary" style={{ fontSize: compact ? 10 : 11 }}>
              <BorderOuterOutlined />
            </Text>
          </Space>
        </Tooltip>
        <Tooltip title="Queue-Treffpunkt (Effet)">
          <Space size={4}>
            <Switch size="small" checked={showCueHitPoint} onChange={handleCueHitPointToggle} />
            <Text type="secondary" style={{ fontSize: compact ? 10 : 11 }}>
              <AimOutlined />
            </Text>
          </Space>
        </Tooltip>
        {isBallEditorAdmin && !compact && (
          <Tooltip title="Aktuellen Aufbau als Preset speichern">
            <Button
              size="small"
              icon={<SaveOutlined />}
              onClick={() => setSaveModalVisible(true)}
              disabled={balls.length === 0}
              style={{ marginLeft: 'auto' }}
            >
              Als Preset speichern
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Save preset button for mobile (separate row) */}
      {isBallEditorAdmin && compact && (
        <div style={{ marginBottom: 8 }}>
          <Button
            size="small"
            icon={<SaveOutlined />}
            onClick={() => setSaveModalVisible(true)}
            disabled={balls.length === 0}
            block
          >
            Als Preset speichern
          </Button>
        </div>
      )}

      {/* Pool Table - Show first on mobile for better UX */}
      <div
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: compact ? 8 : 16,
          display: 'flex',
          justifyContent: 'center',
          background: '#f0f0f0',
        }}
      >
        <PoolTableSVG
          tableSize={tableSize}
          gameType={getGameTypeFromBallSet()}
          ballSet={ballSet}
          scale={scale}
          balls={displayBalls}
          selectedBall={selectedBall}
          onBallClick={handleBallClick}
          onBallDrag={handleBallDrag}
          onTableClick={handleTableClick}
          interactive={true}
          showGrid={showGrid}
          showRack={showRack}
          cueHitPoint={showCueHitPoint ? cueHitPoint : null}
        />
      </div>

      {/* Actions for selected ball */}
      {selectedBall !== null && mode === 'move' && (
        <div style={{ marginBottom: 8 }}>
          <Space>
            <Tag color="blue">
              {ballColors[selectedBall]?.name || `Ball ${selectedBall}`}
            </Tag>
            <Button size="small" icon={<DeleteOutlined />} danger onClick={handleRemoveBall}>
              Entfernen
            </Button>
          </Space>
        </div>
      )}

      {/* Cue hit point selector - collapsible on mobile */}
      {renderCueHitPointSelector()}

      {!compact && <Divider style={{ margin: '12px 0' }} />}

      {/* Ball Selection (only visible in place mode) */}
      {mode === 'place' && (
        <div style={{ marginBottom: compact ? 8 : 16 }}>
          <Text strong style={{ fontSize: 12 }}>Ball auswählen:</Text>
          <div style={{ marginTop: 8 }}>
            {renderBallButtons()}
          </div>
        </div>
      )}

      {mode === 'move' && !compact && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Klicke auf einen Ball auf dem Tisch und ziehe ihn.
          </Text>
        </div>
      )}

      {/* Save Preset Modal (Ball-Editor-Admin only) */}
      <Modal
        title="Aufbau als Preset speichern"
        open={saveModalVisible}
        onOk={handleSavePreset}
        onCancel={() => {
          setSaveModalVisible(false);
          setNewPresetName('');
          setNewPresetDescription('');
        }}
        confirmLoading={saveLoading}
        okText="Speichern"
        cancelText="Abbrechen"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Name *</Text>
            <Input
              placeholder="z.B. 9-Ball Standard-Aufbau"
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div>
            <Text strong>Beschreibung</Text>
            <Input.TextArea
              placeholder="Optionale Beschreibung des Aufbaus..."
              value={newPresetDescription}
              onChange={(e) => setNewPresetDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div>
            <Text type="secondary">
              Ballsatz: <Tag>{BALL_SETS[ballSet]?.name || ballSet}</Tag>
              {balls.length} Bälle
            </Text>
          </div>
        </Space>
      </Modal>
    </Card>
  );
};

export default BallPlacer;
